import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Layout, Layouts } from 'react-grid-layout';

import { type SaveStatus, useDashboardPersistence } from './persistence/useDashboardPersistence';
import { getPresetLayout } from './presets/presetCatalog';
import { usePersonaPreset } from './presets/usePersonaPreset';
import type { DashboardLayoutState, WidgetConfig, WidgetId } from './types';
import { WIDGET_REGISTRY } from './widgetRegistry';
import {
  type WidgetSizePresetId,
  applyWidgetSizePreset,
  DASHBOARD_COLUMNS,
} from './widgetSizePresets';

let instanceCounter = 0;

/** Unique-per-layout instance id so the same widget can be placed more than once. */
function makeInstanceId(widgetId: WidgetId): string {
  instanceCounter += 1;
  return `${widgetId}__${Date.now().toString(36)}_${instanceCounter}`;
}

/**
 * react-grid-layout mutates its layout item objects in place during drag/resize.
 * If we store those same references in React state, RGL sees no change on the next
 * render and discards the user's placement — so we always store fresh clones.
 */
function cloneLayoutItems(items: Layout[] = []): Layout[] {
  return items.map(item => ({ ...item }));
}

function cloneLayouts(layouts: Layouts): Layouts {
  const next: Layouts = {};
  for (const [bp, items] of Object.entries(layouts)) {
    next[bp] = cloneLayoutItems(items);
  }
  return next;
}

/**
 * Finds the first free top-left slot for a w×h tile so a newly added widget packs
 * next to existing ones (scanning left-to-right, top-to-bottom) instead of always
 * starting a fresh row at the bottom.
 */
function findFreeSlot(items: Layout[], w: number, h: number, cols = 12): { x: number; y: number } {
  const overlaps = (x: number, y: number) =>
    items.some(
      item => x < item.x + item.w && x + w > item.x && y < item.y + item.h && y + h > item.y
    );

  for (let y = 0; y < 1000; y += 1) {
    for (let x = 0; x <= cols - w; x += 1) {
      if (!overlaps(x, y)) {
        return { x, y };
      }
    }
  }
  // Fallback (only if the scan is exhausted): drop below every existing tile so the
  // new widget never lands on top of another one when preventCollision is on.
  const bottom = items.reduce((max, item) => Math.max(max, item.y + item.h), 0);
  return { x: 0, y: bottom };
}

export interface UseDashboardLayoutResult {
  layout: DashboardLayoutState;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  addWidget: (widgetId: WidgetId) => void;
  removeWidget: (instanceId: string) => void;
  onLayoutChange: (current: Layout[], allLayouts: Layouts) => void;
  /** RGL drag/resize start — pauses per-tick layout commits for the gesture. */
  onInteractionStart: () => void;
  /** RGL drag/resize stop — commits the final geometry once and persists it. */
  onInteractionStop: (currentLayout: Layout[]) => void;
  setWidgetSize: (instanceId: string, sizeId: WidgetSizePresetId) => void;
  updateWidgetConfig: (instanceId: string, config: WidgetConfig) => void;
  resetToPreset: () => void;
  saveStatus: SaveStatus;
}

/**
 * Owns the dashboard's mutable layout + edit-mode flag, and drives persistence.
 *
 * The seed (currently the default "general" layout; a role-derived preset in a
 * later phase) is hydrated flash-free from localStorage, then reconciled against
 * the per-user server record. Edits persist (debounced) via TableView.customFields;
 * the server record is created lazily on the first real edit.
 */
export function useDashboardLayout(): UseDashboardLayoutResult {
  const { presetId, layout: seed } = usePersonaPreset();
  const { loadedLayout, isLoaded, persist, saveStatus } = useDashboardPersistence(seed);

  const [layout, setLayout] = useState<DashboardLayoutState>(seed);
  const [isEditing, setIsEditingState] = useState(false);
  const isEditingRef = useRef(false);
  const appliedLoadRef = useRef(false);
  const hasLocalEditRef = useRef(false);
  // True only between a drag/resize start and its stop, so onLayoutChange can skip
  // the costly per-tick commit + persist while a gesture is in progress.
  const interactingRef = useRef(false);

  // Apply the persisted layout once, and only before the user makes a local edit,
  // so a slow server read can never clobber a change the user already made.
  useEffect(() => {
    if (isLoaded && !appliedLoadRef.current && !hasLocalEditRef.current) {
      appliedLoadRef.current = true;
      setLayout(loadedLayout);
    }
  }, [isLoaded, loadedLayout]);

  const setIsEditing = useCallback((editing: boolean) => {
    isEditingRef.current = editing;
    setIsEditingState(editing);
  }, []);

  const addWidget = useCallback(
    (widgetId: WidgetId) => {
      const definition = WIDGET_REGISTRY[widgetId];
      if (!definition) {
        return;
      }
      const instanceId = makeInstanceId(widgetId);
      const { w, h, minW, minH } = definition.defaultLayout;

      hasLocalEditRef.current = true;
      setLayout(prev => {
        // Widgets are unique on the dashboard — adding one already present is a no-op
        // (the catalog hides placed widgets; this also guards programmatic callers).
        if (prev.instances.some(inst => inst.widgetId === widgetId)) {
          return prev;
        }
        const breakpoints = Object.keys(prev.layouts).length ? Object.keys(prev.layouts) : ['lg'];
        // Drop the new tile in the first free slot so widgets pack next to each
        // other (computed on the 12-col desktop grid; RGL clamps smaller ones).
        const referenceItems = prev.layouts.lg ?? prev.layouts[breakpoints[0]] ?? [];
        const { x, y } = findFreeSlot(referenceItems, w, h, 12);
        const newItem: Layout = { i: instanceId, x, y, w, h, minW, minH };
        const layouts: Layouts = {};
        for (const bp of breakpoints) {
          layouts[bp] = [...cloneLayoutItems(prev.layouts[bp] ?? []), { ...newItem }];
        }
        const next: DashboardLayoutState = {
          ...prev,
          instances: [...prev.instances, { instanceId, widgetId }],
          layouts,
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const removeWidget = useCallback(
    (instanceId: string) => {
      hasLocalEditRef.current = true;
      setLayout(prev => {
        const layouts: Layouts = {};
        for (const [bp, items] of Object.entries(prev.layouts)) {
          layouts[bp] = cloneLayoutItems(items.filter(item => item.i !== instanceId));
        }
        const next: DashboardLayoutState = {
          ...prev,
          instances: prev.instances.filter(inst => inst.instanceId !== instanceId),
          layouts,
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const onLayoutChange = useCallback(
    (_current: Layout[], allLayouts: Layouts) => {
      // During an active drag/resize, RGL renders the tiles from its own internal
      // state. Mirroring every intermediate layout back into React state — and
      // running a synchronous localStorage write via persist() on each tick — is
      // what made the gesture feel laggy (and fought RGL mid-drag). Skip those
      // ticks; the final geometry is committed once on stop (onInteractionStop).
      if (interactingRef.current) {
        return;
      }
      setLayout(prev => {
        const next: DashboardLayoutState = {
          ...prev,
          layouts: cloneLayouts({ ...prev.layouts, ...allLayouts }),
        };
        // Only persist drag/resize the user actually made (in edit mode); ignore
        // the layout events RGL fires on mount and on breakpoint changes.
        if (isEditingRef.current) {
          hasLocalEditRef.current = true;
          persist(next);
        }
        return next;
      });
    },
    [persist]
  );

  const onInteractionStart = useCallback(() => {
    interactingRef.current = true;
  }, []);

  const onInteractionStop = useCallback(
    (currentLayout: Layout[]) => {
      interactingRef.current = false;
      // Commit the gesture's final geometry once. RGL gives us the single active
      // breakpoint's layout here; clone it so RGL's in-place mutations can't bleed
      // into React state (same reason cloneLayouts exists).
      setLayout(prev => {
        const next: DashboardLayoutState = {
          ...prev,
          layouts: { ...prev.layouts, lg: cloneLayoutItems(currentLayout) },
        };
        if (isEditingRef.current) {
          hasLocalEditRef.current = true;
          persist(next);
        }
        return next;
      });
    },
    [persist]
  );

  const setWidgetSize = useCallback(
    (instanceId: string, sizeId: WidgetSizePresetId) => {
      hasLocalEditRef.current = true;
      setLayout(prev => {
        const layouts: Layouts = {};
        for (const [bp, items] of Object.entries(prev.layouts)) {
          const others = items.filter(item => item.i !== instanceId).map(item => ({ ...item }));
          const target = items.find(item => item.i === instanceId);
          if (!target) {
            layouts[bp] = items.map(item => ({ ...item }));
            continue;
          }
          let resized = applyWidgetSizePreset(target, sizeId);
          // If the larger size would overlap a neighbour, drop it into the first free slot.
          const overlaps = others.some(
            other =>
              resized.x < other.x + other.w &&
              resized.x + resized.w > other.x &&
              resized.y < other.y + other.h &&
              resized.y + resized.h > other.y
          );
          if (overlaps) {
            const slot = findFreeSlot(others, resized.w, resized.h, DASHBOARD_COLUMNS);
            resized = { ...resized, x: slot.x, y: slot.y };
          }
          layouts[bp] = [...others, resized];
        }
        const next: DashboardLayoutState = { ...prev, layouts };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const updateWidgetConfig = useCallback(
    (instanceId: string, config: WidgetConfig) => {
      hasLocalEditRef.current = true;
      setLayout(prev => {
        const next: DashboardLayoutState = {
          ...prev,
          instances: prev.instances.map(inst =>
            inst.instanceId === instanceId
              ? { ...inst, config: { ...inst.config, ...config } }
              : inst
          ),
        };
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const resetToPreset = useCallback(() => {
    hasLocalEditRef.current = true;
    const next = getPresetLayout(presetId);
    setLayout(next);
    persist(next);
  }, [persist, presetId]);

  return useMemo(
    () => ({
      layout,
      isEditing,
      setIsEditing,
      addWidget,
      removeWidget,
      onLayoutChange,
      onInteractionStart,
      onInteractionStop,
      setWidgetSize,
      updateWidgetConfig,
      resetToPreset,
      saveStatus,
    }),
    [
      layout,
      isEditing,
      setIsEditing,
      addWidget,
      removeWidget,
      onLayoutChange,
      onInteractionStart,
      onInteractionStop,
      setWidgetSize,
      updateWidgetConfig,
      resetToPreset,
      saveStatus,
    ]
  );
}
